import React from 'react'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import {
  Alert,
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  type StyleProps,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import * as exportAPI from './export-api'
import { useExportJobs } from './export-items-state'
import { ExportHeader } from './export-result-common'
import { getErrorMessage } from './export-utils'

const styles = {
  tabTitle: {
    typography: 'h5',
    mt: 2,
    mb: 4,
    px: 3,
  },
  table: {
    my: 2,
    '& td:first-of-type, & th:first-of-type': {
      pl: 3,
    },
    '& tbody td': {
      borderBottom: 0,
    },
    '& tbody tr:last-of-type td': {
      borderBottom: '1px solid #f6f7f9',
    },
    '& tbody tr:nth-of-type(odd)': {
      bgcolor: '#f9f9fa',
    },
  },
} satisfies StyleProps

const IssuesTab = () => {
  const { t } = useTranslation('legacy', { keyPrefix: 'export.issues' })
  const [selectedJobId] = useExportJobs((state) => [state.exportDialog.selectedJobId])
  const exportDetails = exportAPI.useGetExportDetails(selectedJobId)
  const exportJob = exportDetails.data?.exportJob

  if (exportDetails.isPending) {
    return (
      <>
        <Box sx={styles.tabTitle}>{t('title')}</Box>

        <Box mx={3}>{t('loading')}</Box>
      </>
    )
  }

  if (exportDetails.error) {
    console.error('Failed to load export job details', exportDetails.error)
    return (
      <>
        <Box sx={styles.tabTitle}>{t('title')}</Box>

        <Box mx={3}>{t('load_failed')}</Box>
      </>
    )
  }

  if (!exportJob?.failedItems.length) {
    return (
      <>
        <Box sx={styles.tabTitle}>{t('title')}</Box>

        <Box mx={3}>{t('no_issues')}</Box>
      </>
    )
  }

  const missingItems = exportJob.failedItems.filter((row) => !row.item).length

  return (
    <>
      <Box sx={styles.tabTitle}>{t('title')}</Box>
      <Box ml={3}>
        <ExportHeader job={exportJob} />
        {missingItems > 0 && (
          <Alert icon={<ErrorRoundedIcon />} severity="error" sx={{ my: 2, alignItems: 'center' }}>
            <Typography display="flex" flexDirection="column" gap={1}>
              <Box sx={{ fontWeight: 500 }}>
                {t('restricted_access_error', { count: missingItems })}
              </Box>
              {t('contact_representative')}
            </Typography>
          </Alert>
        )}
      </Box>
      <Table sx={styles.table}>
        <TableHead>
          <TableRow>
            <TableCell>{t('table.identifier')}</TableCell>
            <TableCell>{t('table.title')}</TableCell>
            <TableCell>{t('table.issue')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {exportJob.failedItems?.map((row) => {
            if (!row.item) return null
            const { item } = row
            return (
              <TableRow key={item.id}>
                <TableCell width={100} sx={{ fontWeight: 'bold', maxWidth: 100 }}>
                  {item.identifier}
                </TableCell>
                <TableCell sx={{ maxWidth: 400 }}>
                  <Typography noWrap>
                    {row.errorDetails.code === 'ProBuilderMappingException' &&
                    row.errorDetails.can_fix ? (
                      <Link
                        color="secondary"
                        underline="hover"
                        href={`/author/item/${item.id}`}
                        target="_blank"
                      >
                        {item.savedContent[0]?.question}
                      </Link>
                    ) : (
                      item.savedContent[0]?.question
                    )}
                  </Typography>
                </TableCell>
                <TableCell width={300} sx={{ maxWidth: 300 }}>
                  {getErrorMessage(row.errorDetails).message || t('unknown_error')}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </>
  )
}

export default IssuesTab
